import { useMemo, useState } from 'react'
import { orderBy, SortDescriptor } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridPageChangeEvent,
  GridSortChangeEvent,
} from '@progress/kendo-react-grid'
import { StandardCommission } from 'api/Trading-Accounts/Types/IntroducingBrokerDetailedProfileDto'
import MoreButton from 'components/MoreButton/MoreButton'
import { useGridState } from 'features/DataGrid'
import { TableStateContextData } from 'utils/TableStateContext'

type InstrumentCommissionsGridProps = {
  standardCommissions: StandardCommission[]
  isEdit: boolean
  handleEditStandardCommissions: (data: StandardCommission) => void
  setFieldValue: (field: string, value: StandardCommission[], shouldValidate?: boolean) => void
  tableData: TableStateContextData
  onHandleGridPageChange(pageNumber: number): void
  onHandleDeleteInstrumentCommission(instrumentCommission: StandardCommission): void
}

type InstrumentCommissionArrayFields =
  | 'instruments'
  | 'instrumentTypes'
  | 'platformTypes'
  | 'tradingAccountTypes'

const isStandardCommissionArrayField = (
  value: string
): value is InstrumentCommissionArrayFields => {
  const validFields: InstrumentCommissionArrayFields[] = [
    'instruments',
    'instrumentTypes',
    'platformTypes',
  ]
  return validFields.includes(value as InstrumentCommissionArrayFields)
}

const normalizeField = (field: string): string => {
  const fieldName = field.split('.')[0]
  return fieldName + 's'
}

export const InstrumentCommissionsGrid: React.FC<InstrumentCommissionsGridProps> = (props) => {
  const { gridState } = useGridState()

  const handlePageChange = (event: GridPageChangeEvent) => {
    props.onHandleGridPageChange(event.page.skip)
  }

  const initialSort: SortDescriptor[] = [{ field: 'platformType.name', dir: 'asc' }]
  const [sort, setSort] = useState(initialSort)

  const arrayOrderBy = (
    normalizedField: InstrumentCommissionArrayFields,
    dir: 'asc' | 'desc' | undefined
  ): StandardCommission[] => {
    const orderedData = [...props.standardCommissions]
    orderedData.sort((a: StandardCommission, b: StandardCommission) => {
      const leftSide = a[normalizedField]?.map((x) => x.name).join(',') || ''
      const rightSide = b[normalizedField]?.map((x) => x.name).join(',') || ''

      return dir === 'asc' ? leftSide.localeCompare(rightSide) : rightSide.localeCompare(leftSide)
    })

    return orderedData
  }

  const sortedData = useMemo(() => {
    const normalizedField = normalizeField(sort[0]?.field || '')
    if (sort.length > 0 && isStandardCommissionArrayField(normalizedField)) {
      return arrayOrderBy(normalizedField, sort[0].dir)
    }
    return orderBy(props?.standardCommissions, sort)
  }, [props?.standardCommissions, sort]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Grid
      pageable
      resizable
      data={sortedData}
      skip={gridState.skip}
      take={gridState.take}
      total={gridState.total}
      onPageChange={handlePageChange}
      data-test='table'
      sort={sort}
      onSortChange={(e: GridSortChangeEvent) => {
        setSort(e.sort.length < 1 ? initialSort : e.sort)
      }}
      sortable
    >
      <Column
        field='platformType.name'
        title='Platform'
        cell={(gridCellProps: GridCellProps) => {
          const standardCommissions = gridCellProps.dataItem as StandardCommission
          return <td>{standardCommissions?.platformTypes?.map((pt) => pt.name).join(',')}</td>
        }}
      />
      <Column
        field='instrument.name'
        title='Instrument'
        cell={(gridCellProps: GridCellProps) => {
          const standardCommissions = gridCellProps.dataItem as StandardCommission
          return <td>{standardCommissions?.instruments?.map((i) => i.symbol).join(',')}</td>
        }}
      />
      <Column
        field='tradingAccountType.name'
        title='Account Type'
        cell={(gridCellProps: GridCellProps) => {
          const standardCommissions = gridCellProps.dataItem as StandardCommission
          return <td>{standardCommissions?.tradingAccountTypes?.map((tt) => tt.name).join(',')}</td>
        }}
      />
      <Column field='rebateCalculationMethod.name' title='Rebate Calc Method' />
      <Column field='amount' title='Rebate Amount' filter='numeric' />
      <Column
        filterable={false}
        title=''
        width={50}
        cell={(gridCellProps: GridCellProps) => (
          <td>
            <MoreButton
              disabled={!props.isEdit}
              items={[
                {
                  name: 'Edit',
                  disabled: !props.isEdit,
                  onClick: () => props.handleEditStandardCommissions(gridCellProps.dataItem),
                },
                {
                  name: 'Delete',
                  disabled: !props.isEdit,
                  onClick: () => {
                    props.onHandleDeleteInstrumentCommission(gridCellProps.dataItem)
                  },
                },
              ]}
            />
          </td>
        )}
      />
    </Grid>
  )
}
